import React, { useCallback, useEffect } from "react";
import {
  Highlights,
  HighlightType,
  HtmlHighlight,
  NewHtmlHighlight,
  useHighlightsStore,
} from "./useHighlightsStore";
import {
  HG_EDITOR_PREVIEW_SCRIPT_PATH,
  HG_EDITOR_SCRIPT_NAME,
  HG_EDITOR_SCRIPT_URL,
  API_URL,
} from "../../../../constants";
import styled from "styled-components";
import { useOrganization } from "hooks/useOrganization";
import { useCustom, useTranslate } from "@refinedev/core";
import { postToIframe } from "./utils";
import { Cuepoint, IRoughNotation } from "../binder/types";
import { useCuepointsStore } from "./useCuepointsStore";
import { useAntTheme } from "hooks/useAntTheme";
import { ViewType } from "../animation/Animation";
import {
  Document,
  PaletteResponse,
  ProjectMediaResponse,
  StoryboardHighlights,
} from "../../types";
import { buildLivePublicationData } from "./helpers";
import { useRemoveHighlight } from "./useRemoveHighlight";
import { useSaveAnimation } from "../animation/hooks/highlights/useSaveAnimation";
import { useParams } from "react-router-dom";
import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage/";
import { reconstructStoryboardHighlights } from "../animation/helpers/helpers";
import { radius } from "@libertify/ui";

const CLASSNAME_PREFIX = "lfhg-hl-"; // libertify heygenius
export const AnimationWeb: React.FC<{
  addHighlight: (
    h: NewHtmlHighlight,
    type: HighlightType
  ) => Promise<{
    highlight: NewHtmlHighlight & { id: string };
    cuepoint: { roughNotation?: IRoughNotation };
  }>;

  pageUrl: string;
  pageId?: string;
  language?: string;
  viewType: ViewType;
  organizationId?: string;
  storyboardHighlights: StoryboardHighlights;
  projectMedia: ProjectMediaResponse;
  projectDocument: Document;
  brandKitPalette: PaletteResponse;
  projectId: string;
}> = ({
  addHighlight,
  pageUrl,
  pageId,
  language,
  viewType,
  organizationId,
  storyboardHighlights,
  projectMedia,
  projectDocument,
  brandKitPalette,
  projectId,
}) => {
  const url = "/proxy";

  const { setCurrentCuepoint, cuepoints, updateCount, setUpdateCount } =
    useCuepointsStore();
  const {
    highlights,
    setCurrentHighlight,
    sectionHighlights,
    highlightedStoryboard,
    markHighlightAsBroken,
  } = useHighlightsStore();
  const { removeHighlight } = useRemoveHighlight(projectId);
  const getHighlightById = useCallback(
    (id: string) => {
      return highlights.find((highlight) => highlight.id === id);
    },
    [highlights]
  );

  const { projectAssets } = useMediaAssetsStorage({
    projectId,
  });

  const params = useParams<{ id: string; mediaId: string }>(); // Add mediaId to the type
  const { mediaId } = params ?? {};
  const storyboardAsset = projectAssets?.find(
    (asset) => asset.asset_type === "Storyboard"
  );
  const storyboardId = storyboardAsset?.id;
  const theme = useAntTheme();
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

  const saveUpdatedData = useSaveAnimation(projectId, mediaId, storyboardId);

  const handleAddHighlight = useCallback(
    async (highlight: NewHtmlHighlight) => {
      // Reload the inspector if user didn't close it before creating new highlight
      setCurrentHighlight(undefined);
      const { highlight: newHighlight, cuepoint } = await addHighlight(
        highlight,
        "html"
      );
      setCurrentCuepoint(newHighlight.id);
      postToIframe({
        type: "highlight-created",
        id: newHighlight.id,
        roughNotation: cuepoint.roughNotation,
      });
    },
    [addHighlight, setCurrentHighlight, setCurrentCuepoint]
  );

  const handleOnClick = useCallback(
    (highlight: HtmlHighlight) => {
      console.debug("Animation handleOnClick highlight", highlight);
      setCurrentHighlight(highlight);
    },
    [setCurrentHighlight]
  );
  const t = useTranslate();
  const iframeReady = React.useRef(false);
  const hasSavedAfterInit = React.useRef(false);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type) {
        console.log("📨 Message from iframe:", event.data.type, event.data);
      }

      if (event.data.type === "highlight-blurred") {
        setCurrentHighlight(undefined);
      } else if (event.data.type === "highlight-clicked") {
        const highlightId = event.data.id;
        const highlight = getHighlightById(highlightId) as HtmlHighlight;
        console.debug("Animation highlight clicked", highlightId, highlight);
        if (highlight) {
          handleOnClick(highlight);
        }
      } else if (event.data.type === "highlight-created") {
        delete event.data.type;
        const eventData = event.data as {
          id: number;
          selector: string;
        };
        const createdHighlight: NewHtmlHighlight = {
          generated: false,
          type: "html",
          selectors: [
            {
              start: eventData.selector,
              text: "",
            },
          ],
        };

        handleAddHighlight(createdHighlight).catch((error) =>
          console.error("Error adding highlight:", error)
        );
      } else if (event.data.type === "highlight-not-found") {
        const highlightId = event.data.id;
        console.debug("Highlight not found:", highlightId);
        if (process.env.VITE_HG_DEBUG) {
          markHighlightAsBroken(highlightId);
        }

        // Find the highlight to remove
        const highlight = getHighlightById(highlightId);
        if (highlight) {
          removeHighlight(highlight, {
            trackAnalytics: false,
            postToIframe: false,
            clearCurrentHighlight: false,
          });

          // Send reindex message after removal
          postToIframe({
            type: "reindex",
          });
        }
      } else if (event.data.type === "highlight-invisible") {
        const highlightId = event.data.id;
        console.debug("Highlight invisible:", highlightId);
        markHighlightAsBroken(highlightId);
      } else if (event.data.type === "iframe-loaded") {
        iframeReady.current = true;
      } else if (event.data.type === "highlights-initialized") {
        if (!hasSavedAfterInit.current && updateCount > 0) {
          console.log("Highlights initialized, saving updated data");
          hasSavedAfterInit.current = true;
          saveUpdatedData(storyboardHighlights);
          setUpdateCount(0);
        }
      }
    };
    globalThis.addEventListener("message", handleMessage);
    return () => globalThis.removeEventListener("message", handleMessage);
  }, [
    highlights,
    handleOnClick,
    setCurrentHighlight,
    handleAddHighlight,
    getHighlightById,
    markHighlightAsBroken,
    removeHighlight,
    updateCount,
    saveUpdatedData,
    storyboardHighlights,
  ]);
  const { organization } = useOrganization({});
  const { data, isLoading: isLoadingPage } = useCustom<{
    payload: string;
  }>({
    url,
    method: "get",
    config: {
      query: {
        url: pageUrl,
      },
    },
    queryOptions: {
      // Fixes MED-4250
      staleTime: 0,
      cacheTime: 0,
    },
  });

  // Helper functions to reduce nesting
  const createIframe = useCallback((viewType: ViewType): HTMLIFrameElement => {
    const iframeId = `hg-editor-iframe-${viewType}`;
    const iframe = globalThis.window.document.createElement("iframe");
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.border = "none";
    iframe.id = iframeId;
    return iframe;
  }, []);

  const createInjectedScript = useCallback(
    (viewType: ViewType, liveData: unknown): HTMLScriptElement => {
      const INJECTED_SCRIPT_URL =
        HG_EDITOR_SCRIPT_URL + "/" + HG_EDITOR_SCRIPT_NAME;
      const injectedScript = globalThis.window.document.createElement("script");

      if (viewType === "editor") {
        injectedScript.type = "module";
        injectedScript.src = INJECTED_SCRIPT_URL;
      } else {
        injectedScript.type = "text/javascript";
        injectedScript.textContent = `
        const widget_base = '${HG_EDITOR_SCRIPT_URL}';
        const script = document.createElement('script');
        script.setAttribute('data-dev', 'allowed');
        const body = window.document.querySelector('body');
        const widgetPath = '${HG_EDITOR_PREVIEW_SCRIPT_PATH}';

          window.heygeniusSettings = {
            widget_base,
            organization_id: '${organizationId}',
            page_id: '${pageId}',
            language: '${language}',
            override_pub_url: '${API_URL}/media/projects/${projectId}',
            is_editor: false,
            liveData: ${JSON.stringify(liveData)},
          }

        script.type = 'module';
        script.src = \`\${widget_base}\${widgetPath}\`;
        body.appendChild(script)

        window.addEventListener('beforeunload', function(event) {
          event.preventDefault();
          event.returnValue = '';
          return '';
        });

        window.addEventListener('message', (event) => {
          if (event.data.type === 'init') {
            // Wait for all resources to load before signaling highlights are ready
            // window.load fires after all resources (images, fonts, scripts, iframes) are loaded
            const notifyReady = () => {
              parent.postMessage(
                { type: "highlights-initialized" },
                "*"
              );
            };

            if (document.readyState === 'complete') {
              notifyReady();
            } else {
              window.addEventListener('load', notifyReady, { once: true });
            }
          }
        })
        parent.postMessage(
          {
            type: "iframe-loaded",
          },
          "*"
        );
      `;
      }

      return injectedScript;
    },
    [organizationId, pageId, language, projectId]
  );

  const processHtmlContent = useCallback(
    (html: string, pageUrl: string): string => {
      const pageUrlObj = new globalThis.window.URL(pageUrl);
      const hostname = pageUrlObj.origin;

      // Block existing heygenius script in the page to prevent conflicts with the editor
      // monkey patch document.createElement to block heygenius script without data-dev attribute
      const heygeniusWidgetBlocker = `
const _originalCreateElement = document.createElement;
document.createElement = function(tagName) {
  const element = _originalCreateElement.call(document, tagName);
  
  if (tagName.toLowerCase() === 'script') {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
    const originalSrcSetter = descriptor?.set;
    
    Object.defineProperty(element, 'src', {
      set: function(value) {
        // Check if script has data-dev attribute
        const hasDataDev = this.hasAttribute('data-dev');
        
        if (value && value.includes('heygenius.ai') && !hasDataDev) {
          return; // BLOCKED
        }
        
        // Call the original setter
        if (originalSrcSetter) {
          originalSrcSetter.call(this, value);
        }
      },
      get: function() {
        return descriptor?.get?.call(this);
      },
      configurable: true
    });
  }
  
  return element;
};`;
      // Replace relative URLs with absolute URLs of the proxied page
      // Base tag doesn't work here because of the security context (but
      // base tag will work in localhost or when the server is on the
      // loopback address)
      return (
        html
          .replaceAll(
            /<(link|script|img)([^>]*(?:src|href)=["'])(?!(?:https?)|\/\/)(.*?)"/gi,
            (_match, tag, attrs, url) => {
              // Convert relative URL to absolute URL
              const absoluteUrl = url.startsWith("/")
                ? `${hostname}${url}`
                : `${hostname}/${url}`;
              return `<${tag}${attrs}${absoluteUrl}"`;
            }
          )
          // block existing customer widget settings and script
          .replaceAll(
            "window.heygeniusSettings",
            heygeniusWidgetBlocker + "\nwindow.heygeniusSettings_blocked"
          )
      );
    },
    []
  );

  const createLiveData = useCallback(
    (viewType: ViewType) => {
      if (viewType !== "preview" && viewType !== "standalone-preview") {
        return;
      }

      const reconstructHighlightsAsset = reconstructStoryboardHighlights(
        sectionHighlights,
        highlights,
        cuepoints,
        storyboardHighlights,
        highlightedStoryboard as Parameters<
          typeof reconstructStoryboardHighlights
        >[4]
      );
      return buildLivePublicationData(
        reconstructHighlightsAsset ?? storyboardHighlights,
        projectMedia,
        projectDocument,
        brandKitPalette,
        projectId
      );
    },
    [
      sectionHighlights,
      highlights,
      cuepoints,
      storyboardHighlights,
      highlightedStoryboard,
      projectMedia,
      projectDocument,
      brandKitPalette,
      projectId,
    ]
  );

  const setupIframe = useCallback(() => {
    const iframeId = `hg-editor-iframe-${viewType}`;
    const iframeExists = globalThis.window.document.getElementById(iframeId);
    if (iframeExists) return;
    if (!organization?.id) return;

    const iframe = createIframe(viewType);
    iframeRef.current = iframe;

    const liveData = createLiveData(viewType);

    const injectedScript = createInjectedScript(viewType, liveData);
    const containerId =
      viewType === "preview" || viewType === "standalone-preview"
        ? "iframe-preview-container"
        : "iframe-container";

    const processedHtml = processHtmlContent(data?.data.payload || "", pageUrl);
    iframe.srcdoc = processedHtml;

    globalThis.window.document.getElementById(containerId)?.appendChild(iframe);

    const handleOnLoad = handleOnLoadFactory(
      iframe,
      injectedScript,
      iframeReady,
      highlights,
      createStrings(t),
      createTheme(theme),
      cuepoints
    );
    iframe.addEventListener("load", handleOnLoad);
  }, [
    viewType,
    organization?.id,
    createIframe,
    createInjectedScript,
    processHtmlContent,
    data?.data.payload,
    pageUrl,
    createLiveData,
    highlights,
    t,
    theme,
    cuepoints,
  ]);

  useEffect(() => {
    if (isLoadingPage) return;
    iframeReady.current = false;
    try {
      setupIframe();
    } catch (error) {
      console.error("Error loading iframe:", error);
    }
  }, [isLoadingPage, setupIframe]);
  return (
    <StyledWrapper>
      <div
        key={viewType}
        style={{ width: "100%", height: "100%" }}
        id={
          viewType === "preview" || viewType === "standalone-preview"
            ? "iframe-preview-container"
            : "iframe-container"
        }
      />
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 200px);
`;
function injectIframeCSS(
  iframe: HTMLIFrameElement,
  theme: ReturnType<typeof createTheme>
): void {
  const injectedCSS = `
    <style>
      .${CLASSNAME_PREFIX}id {
        background-color: ${theme.highlightBadgeUnselected};
        position: absolute;
        top: 0;
        left: -3px;
        margin: 0;
        padding: 3px;
        border-bottom: none;
        width: max-content;
        height: max-content;
        border-radius: ${radius.pill}px;
        color: white;
      }
    </style>`;
  iframe.contentDocument.head.insertAdjacentHTML("beforeend", injectedCSS);
}

function processHighlightsWithNotation(
  highlights: Highlights,
  cuepoints: Cuepoint[]
): HtmlHighlight[] {
  return (highlights as HtmlHighlight[]).map((highlight) => {
    const cuepoint = cuepoints.find(
      (cuepoint) => cuepoint.sourceId === highlight.id
    );
    return {
      ...highlight,
      roughNotation: cuepoint?.roughNotation,
    };
  });
}

function initializeIframeContent(
  _iframe: HTMLIFrameElement,
  highlights: Highlights,
  strings: unknown,
  theme: ReturnType<typeof createTheme>,
  cuepoints: Cuepoint[]
): void {
  console.log("Iframe processed.");
  const params: Record<string, unknown> = { highlights, strings, theme };
  const highlightsWithNotation = processHighlightsWithNotation(
    highlights,
    cuepoints
  );
  params.highlights = highlightsWithNotation;
  postToIframe({ type: "init", ...params });
}

function checkIframeReady(
  iframe: HTMLIFrameElement,
  iframeReady: React.MutableRefObject<boolean>,
  highlights: Highlights,
  strings: unknown,
  theme: ReturnType<typeof createTheme>,
  cuepoints: Cuepoint[]
): void {
  const state = iframe.contentDocument.readyState;
  const done = state === "complete";

  if (done && iframeReady.current) {
    initializeIframeContent(iframe, highlights, strings, theme, cuepoints);
  } else {
    setTimeout(
      () =>
        checkIframeReady(
          iframe,
          iframeReady,
          highlights,
          strings,
          theme,
          cuepoints
        ),
      100
    );
  }
}

function handleOnLoadFactory(
  iframe: HTMLIFrameElement,
  injectedScript: HTMLScriptElement,
  iframeReady: React.MutableRefObject<boolean>,
  highlights: Highlights,
  strings: unknown,
  theme: ReturnType<typeof createTheme>,
  cuepoints: Cuepoint[]
): (ev: Event) => void {
  return () => {
    injectIframeCSS(iframe, theme);
    iframe.contentDocument.body.appendChild(injectedScript);

    console.debug("Iframe loaded, checking ready state...");
    checkIframeReady(
      iframe,
      iframeReady,
      highlights,
      strings,
      theme,
      cuepoints
    );
  };
}

function createStrings<T extends (key: string) => string>(
  t: T
): Record<string, string> {
  return {
    addHighlight: t("components.binder.AnimationWeb.addHighlight"),
  };
}

function createTheme(theme: { colorPrimary: string }): Record<string, string> {
  return {
    highlightBadgeSelected: theme.colorPrimary,
    highlightBadgeUnselected: "rgba(0, 0, 0, 0.8)",
  };
}
